import React from 'react';
import {
  ClipboardCopy,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Title,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ConfigureFormValues } from './ConfigureStep';
import { PreviewService, MirrordOverrides } from '../helpers/apiHelpers';

interface ReviewStepProps {
  config: ConfigureFormValues;
  selectedServices: PreviewService[];
  selectedInfra?: PreviewService[];
  dbBranch?: string;
  serviceOverrides?: Record<string, MirrordOverrides>;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({ config, selectedServices, selectedInfra = [], dbBranch, serviceOverrides = {} }) => {
  const routingHeader = `Baggage: mirrord-session=${config.previewTag}`;

  return (
    <>
      <Title headingLevel="h3" size="md" style={{ marginBottom: 16 }}>
        Preview Session Summary
      </Title>

      <DescriptionList isHorizontal columnModifier={{ default: '2Col' }} style={{ marginBottom: 24 }}>
        <DescriptionListGroup>
          <DescriptionListTerm>Feature Branch(es)</DescriptionListTerm>
          <DescriptionListDescription>{config.resolvedBranches.filter(Boolean).join(', ')}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Namespace</DescriptionListTerm>
          <DescriptionListDescription>{config.namespace}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Preview Tag</DescriptionListTerm>
          <DescriptionListDescription>{config.previewTag}</DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>TTL</DescriptionListTerm>
          <DescriptionListDescription>{config.ttl}</DescriptionListDescription>
        </DescriptionListGroup>
        {dbBranch && !config.resolvedBranches.includes(dbBranch) && (
          <DescriptionListGroup>
            <DescriptionListTerm>DB Branch</DescriptionListTerm>
            <DescriptionListDescription>{dbBranch}</DescriptionListDescription>
          </DescriptionListGroup>
        )}
        <DescriptionListGroup>
          <DescriptionListTerm>Routing Header</DescriptionListTerm>
          <DescriptionListDescription>
            <ClipboardCopy isReadOnly hoverTip="Copy" clickTip="Copied">
              {routingHeader}
            </ClipboardCopy>
          </DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>

      <Title headingLevel="h3" size="md" style={{ marginBottom: 12 }}>
        Selected Overlay Services ({selectedServices.length})
      </Title>

      <Table aria-label="Selected overlay services" variant="compact">
        <Thead>
          <Tr>
            <Th>Service</Th>
            <Th>Image</Th>
            <Th>Type</Th>
            <Th>Overrides</Th>
          </Tr>
        </Thead>
        <Tbody>
          {selectedServices.map((svc) => {
            const ov = serviceOverrides[svc.name]?.feature;
            const envCount = Object.keys(ov?.env?.override ?? {}).length;
            const dbEnabled = (ov?.db_branches?.length ?? 0) > 0;
            const kafkaEnabled = Object.keys(ov?.split_queues ?? {}).length > 0;
            const hasOverrides = envCount > 0 || dbEnabled || kafkaEnabled;
            return (
              <Tr key={svc.name}>
                <Td>{svc.name}</Td>
                <Td>{svc.image || '—'}</Td>
                <Td>{svc.type || 'deployment'}</Td>
                <Td>
                  {hasOverrides ? (
                    <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {envCount > 0 && <span style={{ background: '#E3FCEF', color: '#00875A', fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>Env +{envCount}</span>}
                      {dbEnabled && <span style={{ background: '#DEEBFF', color: '#0052CC', fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>DB</span>}
                      {kafkaEnabled && <span style={{ background: '#fdf4e7', color: '#7d5700', fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>Kafka</span>}
                    </span>
                  ) : <span style={{ color: '#5E6C84', fontSize: 12 }}>none</span>}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      {selectedInfra.length > 0 && (
        <>
          <Title headingLevel="h3" size="md" style={{ marginBottom: 12, marginTop: 24 }}>
            Selected Infrastructure Services ({selectedInfra.length})
          </Title>
          <Table aria-label="Selected infra services" variant="compact">
            <Thead>
              <Tr>
                <Th>Service</Th>
                <Th>Image</Th>
                <Th>Type</Th>
              </Tr>
            </Thead>
            <Tbody>
              {selectedInfra.map((svc) => (
                <Tr key={svc.name}>
                  <Td>{svc.name}</Td>
                  <Td>{svc.image || '—'}</Td>
                  <Td>{svc.type || 'deployment'}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </>
      )}
    </>
  );
};
